import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Panel, inputCls, copyText } from "@/components/kit";
import { api, errMsg, API } from "@/lib/api";
import { SPORTS } from "@/pages/Signup";
import { SectionHead } from "./common";
import { ImageUpload } from "@/components/ImageUpload";
import { THEMES, teamInk } from "@/lib/theme";

const KEYS = ["name", "sport", "location", "tagline", "about", "primary_color", "secondary_color", "logo_url", "hero_image_url", "theme"];
const SWATCHES = ["#CCFF00", "#FF4500", "#00E5FF", "#10B981", "#EF4444", "#F59E0B", "#3B82F6", "#E11D48"];

const AppLinks = ({ team }) => {
  const fan = `${window.location.origin}/t/${team.slug}`;
  const player = `${fan}/players`;
  return (
    <Panel className="space-y-4">
      <p className="font-display text-xl font-bold uppercase text-white">Your two apps</p>
      {[["Fan hub app", fan, "fan"], ["Player app", player, "player"]].map(([label, url, app]) => (
        <div key={app} className="flex items-center gap-4">
          <img src={`${API}/public/teams/${team.slug}/icon.png?app=${app}&size=192&v=${(team.logo_url || "").slice(-12)}`} alt={`${label} icon`} className="h-12 w-12 rounded-lg" data-testid={`app-icon-${app}`} />
          <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-white">{label}</p><p className="truncate font-mono text-xs text-slate-400">{url}</p></div>
          <Button size="sm" variant="outline" className="rounded-full border-white/15 bg-transparent" onClick={() => copyText(url)} data-testid={`copy-${app}-app-link`}>Copy</Button>
        </div>
      ))}
      <p className="text-xs text-slate-500">Share these links. Visitors can tap "Install" to save each as its own app on their phone.</p>
    </Panel>
  );
};

const ThemePicker = ({ value, color, onChange }) => (
  <div className="grid gap-3 sm:grid-cols-3">
    {THEMES.map((t) => (
      <button type="button" key={t.key} onClick={() => onChange(t.key)} data-testid={`theme-option-${t.key}`}
        className={`overflow-hidden rounded-lg border-2 text-left transition-transform hover:-translate-y-0.5 ${value === t.key ? "border-[#CCFF00]" : "border-white/10"}`}>
        <div className="p-3" style={{ background: t.bg }}>
          <p className="font-display text-lg font-black uppercase leading-none" style={{ color: t.text }}>Matchday</p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-widest" style={{ color: teamInk(color, t.key) }}>Next up · Sun 15:00</p>
          <div className="mt-2 rounded px-2 py-1.5 text-[11px]" style={{ background: t.card, color: t.muted }}>Fixtures, news & forms</div>
        </div>
        <div className="bg-[#0B0F17] px-3 py-2"><p className="text-sm font-semibold text-white">{t.label}</p><p className="text-[11px] text-slate-400">{t.desc}</p></div>
      </button>
    ))}
  </div>
);

export default function TeamSettings({ teamId, team, reloadTeam, overview, setTab }) {
  const canTheme = overview.features.themes;
  const [f, setF] = useState(Object.fromEntries(KEYS.map((k) => [k, team[k] || (k === "theme" ? "dark" : "")])));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/teams/${teamId}`, f);
      toast.success("Hub updated");
      reloadTeam();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6">
      <SectionHead title="Hub & settings" text="Branding and details shown on your public fan hub." />
      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <Panel>
          <form onSubmit={save} className="space-y-4" data-testid="settings-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Team name"><Input required value={f.name} onChange={set("name")} data-testid="settings-name-input" /></Field>
              <Field label="Sport"><select className={inputCls} value={f.sport} onChange={set("sport")} data-testid="settings-sport-select">{SPORTS.map((s) => <option key={s}>{s}</option>)}</select></Field>
            </div>
            <Field label="Ground / town"><Input value={f.location} onChange={set("location")} data-testid="settings-location-input" /></Field>
            <Field label="Tagline"><Input value={f.tagline} onChange={set("tagline")} data-testid="settings-tagline-input" /></Field>
            <Field label="About the club"><textarea className={`${inputCls} h-24`} value={f.about} onChange={set("about")} data-testid="settings-about-input" /></Field>
            {!canTheme && (
              <p className="rounded-md border border-[#CCFF00]/30 p-3 text-sm text-slate-300" data-testid="theme-locked-note">
                Custom colours and light themes are on higher plans. <button type="button" className="font-semibold text-[#CCFF00]" onClick={() => setTab("billing")}>See plans</button>
              </p>
            )}
            <fieldset disabled={!canTheme} className="space-y-4 disabled:opacity-40">
            <Field label="Team colour">
              <div className="flex flex-wrap items-center gap-2">
                {SWATCHES.map((c) => <button type="button" key={c} onClick={() => setF({ ...f, primary_color: c })} className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${f.primary_color.toUpperCase() === c ? "border-white" : "border-transparent"}`} style={{ background: c }} data-testid={`swatch-${c.slice(1)}`} aria-label={c} />)}
                <input type="color" value={f.primary_color} onChange={set("primary_color")} className="h-8 w-12 cursor-pointer rounded bg-transparent" data-testid="settings-color-input" />
              </div>
            </Field>
            <Field label="Hub & app theme" hint="Applies to your fan hub and player app. Text and accent colours adjust automatically for contrast.">
              <ThemePicker value={f.theme} color={f.primary_color} onChange={(v) => setF({ ...f, theme: v })} />
            </Field>
            </fieldset>
            <Field label="Team logo" hint="Square PNG or JPG works best"><ImageUpload value={f.logo_url} onChange={(v) => setF({ ...f, logo_url: v })} teamId={teamId} testid="settings-logo" contain /></Field>
            <Field label="Hero photo" hint="Wide action shot, at least 1600px across. Leave empty for the default."><ImageUpload value={f.hero_image_url} onChange={(v) => setF({ ...f, hero_image_url: v })} teamId={teamId} testid="settings-hero" wide /></Field>
            <Button type="submit" className="rounded-full px-8 font-bold uppercase" data-testid="settings-save-btn">Save</Button>
          </form>
        </Panel>
        <AppLinks team={team} />
      </div>
    </div>
  );
}
